/**
 * FleetMap — capa Leaflet del módulo GPS (mockup).
 *
 * Reemplaza al `GpsFleetMap` de la new front: misma idea visual (auto rotado
 * según rumbo, anillo de selección, geocercas circulares + poligonales) pero
 * adaptado al stack local:
 *  - usa el shim propio `lib/react-leaflet-lite` (sin react-leaflet por
 *    licencia Hippocratic),
 *  - el basemap viene de `config/basemaps` (env-driven, hot-swappable).
 */
import { useEffect, useMemo } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  MapContainer, TileLayer, Marker, Circle, Polygon, useMap,
} from '../../../../lib/react-leaflet-lite';
import { useBasemapTile } from '../../../../config/basemaps';
import { useCountry } from '../../../../context/CountryContext';
import { geocodeDefaultsFor } from '../../../../utils/geocode';
import { estadoMeta } from '../gpsMock';
import { useVehicleType } from '../vehicleTypes';
import { createCircleMarker } from '../../../../utils/mapHelpers';

// Construye el marker con el Material Symbol del TIPO de vehículo del device
// (camión, furgón, auto, moto, bici, …). El símbolo se renderiza vía la fuente
// `material-symbols-outlined` (cargada globalmente). El glyph queda upright; el
// rumbo se ve en el panel de detalle.
// Migrado a createCircleMarker (audit #8). El helper provee la misma estructura
// (anillo rojo si alarm, anillo naranja si selected, sombra default) y soporta
// glyph Material Symbol.
function deviceIcon(materialIcon, color, selected, alarm) {
  const size = selected ? 42 : 34;
  return createCircleMarker({
    size,
    color,
    borderColor: '#fff',
    borderWidth: 3,
    glyph: materialIcon,
    glyphFont: 'material',
    glyphColor: '#fff',
    selected,
    // `alarm:true` dibuja el halo rojo SOLID (box-shadow). No usamos `pulse`
    // para preservar el visual previo de gpsTracking (anillo estático rojo).
    alarm,
  });
}

function DeviceMarker({ device, live, selected, onSelect }) {
  const pos = live?.pos ?? device.pos;
  const alarm = (device.alarmasActivas?.length ?? 0) > 0;
  const vehicleType = useVehicleType(device);
  const color = estadoMeta[device.estado].hex;
  const icon = useMemo(
    () => deviceIcon(vehicleType.materialIcon, color, selected, alarm),
    [vehicleType.materialIcon, color, selected, alarm],
  );
  return (
    <Marker
      position={[pos.lat, pos.lng]}
      icon={icon}
      zIndexOffset={selected ? 1000 : alarm ? 500 : 0}
      eventHandlers={{ click: () => onSelect?.(device.id) }}
    />
  );
}

function GeocercaShape({ geocerca }) {
  const opts = {
    color: geocerca.color,
    weight: 1.5,
    fillColor: geocerca.color,
    fillOpacity: 0.1,
    dashArray: geocerca.alertaActiva ? undefined : '6 6',
  };
  if (geocerca.tipo === 'circular' && geocerca.centro && geocerca.radioM) {
    return <Circle center={[geocerca.centro.lat, geocerca.centro.lng]} radius={geocerca.radioM} {...opts} />;
  }
  if (geocerca.poligono) {
    return <Polygon positions={geocerca.poligono.map((p) => [p.lat, p.lng])} {...opts} />;
  }
  return null;
}

function MapController({ devices, geocercas, live, focusId }) {
  const map = useMap();

  // Encuadre inicial a toda la flota con posición conocida (corre una vez).
  useEffect(() => {
    if (!map) return;
    const pts = devices
      .filter((d) => d.estado !== 'sin_senal' || d.pos)
      .map((d) => [d.pos.lat, d.pos.lng]);
    if (pts.length > 0) {
      map.fitBounds(L.latLngBounds(pts).pad(0.15), { animate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Vuela al elemento enfocado. `focusId` puede apuntar a un device (consola de
  // monitoreo) o a una geocerca (vista de geocercas); resolvemos primero device
  // y, si no hay match, encuadramos el perímetro de la geocerca.
  useEffect(() => {
    if (!map || !focusId) return;

    const d = devices.find((x) => x.id === focusId);
    if (d) {
      const pos = live[focusId]?.pos ?? d.pos;
      map.flyTo([pos.lat, pos.lng], Math.max(map.getZoom(), 14), { duration: 0.6 });
      return;
    }

    const g = geocercas.find((x) => x.id === focusId);
    if (!g) return;
    if (g.tipo === 'circular' && g.centro && g.radioM) {
      // Encuadra el círculo completo (bounds del diámetro) con margen.
      const bounds = L.latLng(g.centro.lat, g.centro.lng).toBounds(g.radioM * 2);
      map.flyToBounds(bounds.pad(0.2), { duration: 0.6 });
    } else if (g.poligono?.length) {
      const pts = g.poligono.map((p) => [p.lat, p.lng]);
      map.flyToBounds(L.latLngBounds(pts).pad(0.2), { duration: 0.6 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, map]);

  return null;
}

export default function FleetMap({
  devices = [],
  geocercas = [],
  live = {},
  selectedId = null,
  focusId = null,
  showGeocercas = true,
  isDark = false,
  onSelect,
  className,
}) {
  const tile = useBasemapTile(isDark);
  const { countryCode } = useCountry();
  // Centro inicial del país de operación (Santiago para CL, Buenos Aires para
  // AR): solo es el encuadre de arranque, MapController vuela a los dispositivos
  // o geocercas apenas hay un foco.
  const { center } = geocodeDefaultsFor(countryCode);
  return (
    <div className={className} style={{ position: 'absolute', inset: 0 }}>
      <MapContainer
        center={[center.lat, center.lng]}
        zoom={11}
        scrollWheelZoom
        zoomControl={false}
        style={{ height: '100%', width: '100%' }}
        className="z-0 bg-bg-muted"
      >
        <TileLayer
          key={tile.url}
          url={tile.url}
          attribution={tile.attribution}
          subdomains={tile.subdomains}
          maxZoom={tile.maxZoom}
        />
        {showGeocercas && geocercas.map((g) => <GeocercaShape key={g.id} geocerca={g} />)}
        {devices
          .filter((d) => d.estado !== 'sin_senal' || (d.alarmasActivas?.length ?? 0) > 0)
          .map((d) => (
            <DeviceMarker
              key={d.id}
              device={d}
              live={live[d.id]}
              selected={selectedId === d.id}
              onSelect={onSelect}
            />
          ))}
        <MapController devices={devices} geocercas={geocercas} live={live} focusId={focusId} />
      </MapContainer>
    </div>
  );
}
